import type { TAddress } from "@/api/shared/address";
import type { TPhone } from "@/api/shared/contacts.ts";
import type { AssistantData } from "@/types/assistant";
import { useCompanyMeList } from "@/composables/models/company/useCompanyMe";
import { usePracticeMeList } from "@/composables/models/practice/usePracticeMe";
import { useAuthStore } from "@/stores/authStore.ts";
import { resolveDefaultPhonePrefix, prefixFromAddressCountry } from "@/utils/phoneMaskUtils";
import { storeToRefs } from "pinia";
import { computed, inject } from "vue";
import { useI18n } from "vue-i18n";

function phonePrefixFromPhones(phones?: TPhone[] | null): string | null {
  const value = phones?.[0]?.prefix;
  return value != null && value !== "" ? value : null;
}

function countryFromAddresses(addresses?: TAddress[] | null): string | null {
  if (!addresses?.length) return null;
  const primary =
    addresses.find((address) => address.is_primary || (address as { primary?: boolean }).primary) ??
    addresses[0];
  return primary?.country ?? null;
}

function uniqueIds(ids: Array<string | undefined | null>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function orderByIds<T extends { id: string }>(items: T[] | undefined, ids: string[]): T[] {
  if (!items?.length) return [];
  if (!ids.length) return items;
  const byId = new Map(items.map((item) => [item.id, item]));
  const ordered = ids.flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
  const seen = new Set(ordered.map((item) => item.id));
  return [...ordered, ...items.filter((item) => !seen.has(item.id))];
}

function phonePrefixFromEntities(entities: Array<{ phones?: TPhone[] | null }>): string | null {
  for (const entity of entities) {
    const prefix = phonePrefixFromPhones(entity.phones);
    if (prefix) return prefix;
  }
  return null;
}

function countryFromEntities(entities: Array<{ addresses?: TAddress[] | null }>): string | null {
  for (const entity of entities) {
    const country = countryFromAddresses(entity.addresses);
    if (country) return country;
  }
  return null;
}

function countryFromCompanyFields(companies: Array<{ country?: string | null }>): string | null {
  for (const company of companies) {
    if (company.country) return company.country;
  }
  return null;
}

function pickAssistantPractice(data: AssistantData | null) {
  return data?.gpPractice ?? data?.orthoPractice ?? null;
}

function pickAssistantCompany(data: AssistantData | null) {
  return data?.gpCompany ?? data?.orthoCompany ?? null;
}

/** True when practice phone or address country alone can pick a prefix (no staff/locale fallbacks). */
function practicePathResolves(
  practicePhonePrefix: string | null,
  practiceAddressCountry: string | null
): boolean {
  if (practicePhonePrefix) return true;
  return !!prefixFromAddressCountry(practiceAddressCountry);
}

/** True when company phone or country fields alone can pick a prefix (no staff/locale fallbacks). */
function companyPathResolves(input: {
  companyPhonePrefix: string | null;
  companyAddressCountry: string | null;
  companyCountry: string | null | undefined;
}): boolean {
  if (input.companyPhonePrefix) return true;
  if (prefixFromAddressCountry(input.companyCountry)) return true;
  return !!prefixFromAddressCountry(input.companyAddressCountry);
}

export function useDefaultPhonePrefix() {
  const { locale } = useI18n();
  const authStore = useAuthStore();
  const { loggedAsStaff, isLoggedIn } = storeToRefs(authStore);
  const assistantData = inject<AssistantData | null>("assistantData", null);

  const assistantPractice = computed(() => pickAssistantPractice(assistantData));
  const assistantCompany = computed(() => pickAssistantCompany(assistantData));

  const assistantPracticePhonePrefix = computed(() =>
    phonePrefixFromPhones(assistantPractice.value?.phones)
  );
  const assistantPracticeAddressCountry = computed(() =>
    countryFromAddresses(assistantPractice.value?.addresses)
  );

  const assistantResolvesPractice = computed(() => {
    if (!assistantPractice.value) return false;
    return practicePathResolves(
      assistantPracticePhonePrefix.value,
      assistantPracticeAddressCountry.value
    );
  });

  /** Prefer `/me` linked-practice order when present; list response is the source of truth for data. */
  const staffPracticeIds = computed(() => {
    const staff = loggedAsStaff.value;
    if (!staff) return [];
    return uniqueIds([
      ...(staff.linked_practices ?? []).map((v) => v.practice_id),
      ...(staff.companies?.flatMap((company) => company.practices) ?? []),
    ]);
  });

  const staffCompanyIds = computed(() =>
    uniqueIds(loggedAsStaff.value?.companies?.map((company) => company.id) ?? [])
  );

  const linkedPracticeListEnabled = computed(
    () => isLoggedIn.value && !assistantResolvesPractice.value
  );

  const { data: linkedPractices } = usePracticeMeList(
    { only_linked_practices: true },
    linkedPracticeListEnabled,
    { suppressErrors: true }
  );

  const orderedPractices = computed(() =>
    orderByIds(linkedPractices.value, staffPracticeIds.value)
  );

  const practicePhonePrefix = computed(() => {
    if (assistantPracticePhonePrefix.value) return assistantPracticePhonePrefix.value;
    return phonePrefixFromEntities(orderedPractices.value);
  });

  const practiceAddressCountry = computed(() => {
    if (assistantPracticeAddressCountry.value) return assistantPracticeAddressCountry.value;
    return countryFromEntities(orderedPractices.value);
  });

  const assistantCompanyPhonePrefix = computed(() =>
    phonePrefixFromPhones(assistantCompany.value?.phones)
  );
  const assistantCompanyAddressCountry = computed(() =>
    countryFromAddresses(assistantCompany.value?.addresses)
  );
  const assistantCompanyCountry = computed(() => assistantCompany.value?.country ?? null);

  const companyListEnabled = computed(() => {
    if (!isLoggedIn.value) return false;
    if (practicePathResolves(practicePhonePrefix.value, practiceAddressCountry.value)) {
      return false;
    }
    if (
      assistantCompany.value &&
      companyPathResolves({
        companyPhonePrefix: assistantCompanyPhonePrefix.value,
        companyAddressCountry: assistantCompanyAddressCountry.value,
        companyCountry: assistantCompanyCountry.value,
      })
    ) {
      return false;
    }
    return true;
  });

  const { data: linkedCompanies } = useCompanyMeList({}, companyListEnabled, {
    suppressErrors: true,
  });

  const orderedCompanies = computed(() => orderByIds(linkedCompanies.value, staffCompanyIds.value));

  const companyPhonePrefix = computed(() => {
    if (assistantCompanyPhonePrefix.value) return assistantCompanyPhonePrefix.value;
    return phonePrefixFromEntities(orderedCompanies.value);
  });

  const companyAddressCountry = computed(() => {
    if (assistantCompanyAddressCountry.value) return assistantCompanyAddressCountry.value;
    return countryFromEntities(orderedCompanies.value);
  });

  const companyCountry = computed(() => {
    if (assistantCompanyCountry.value) return assistantCompanyCountry.value;
    return countryFromCompanyFields(orderedCompanies.value);
  });

  const defaultPrefixResolution = computed(() => {
    return resolveDefaultPhonePrefix({
      practicePhonePrefix: practicePhonePrefix.value,
      practiceAddressCountry: practiceAddressCountry.value,
      companyPhonePrefix: companyPhonePrefix.value,
      companyAddressCountry: companyAddressCountry.value,
      companyCountry: companyCountry.value,
      staffPhonePrefix: phonePrefixFromPhones(loggedAsStaff.value?.phones),
      browserLocale: locale.value,
    });
  });

  return { defaultPrefixResolution };
}
